import type {
  DurationFormatType,
  IEntityTimesSettings,
  IFrameworkViewSetting,
  IProjectTaskListSetting,
  IProjectTimeTrackingTrackedPlannedSetting,
  IProjectViewSetting,
  ITaskViewListsSetting,
  IViewPersistenceSetting,
  ListEntity,
  UserNameDisplayFormat,
  EntityTimesViewContext
} from '@awork/framework/models/view-setting.model'
import {
  VIEW_PERSISTENCE_USER_SETTINGS,
  VIEW_PERSISTENCE_VALUE_SETTINGS
} from '@awork/framework/models/view-setting.model'
import { SignalQuery } from '@awork/core/state/signal-store/signalQuery'
import { SettingsStore } from '@awork/framework/state/settings.store'
import type { OnDestroy, Signal } from '@angular/core'
import { computed, Injectable, inject } from '@angular/core'
import type { ValidStandardTimeReportEntityType } from '@awork/features/time-tracking/models/types'
import type { EntityName } from '@awork/_shared/services/user-settings-service/user-settings.service'
import type {
  EntitySettings,
  EntitySettingsKey,
  EntitySettingsValue,
  IGroupingSorting,
  NotificationUserSetting,
  SettingsState
} from '@awork/framework/state/settings.types'
import type { IDesktopAppUserSetting } from '@awork/_shared/models/user-setting.model'

export const DEFAULT_FRAMEWORK_VIEW_SETTINGS: IFrameworkViewSetting = {
  darkMode: 'auto',
  menuPinned: true,
  menuWidth: 280,
  menuCompact: false,
  menuItemsExpandedState: [],
  hideTimers: false,
  keepNotificationOnNavigation: false,
  showMenuFavoritesSection: true,
  showMenuExternalProjectsSection: true,
  showMenuMyProjectsSection: true
}

@Injectable({ providedIn: 'root' })
export class SettingsQuery extends SignalQuery<SettingsState> implements OnDestroy {
  static instance: SettingsQuery

  protected store: SettingsStore

  constructor() {
    const store = inject(SettingsStore)

    super(store)

    this.store = store
    SettingsQuery.instance = this
  }

  /** Clears the model-helper reference when its injector is destroyed. */
  ngOnDestroy(): void {
    if (SettingsQuery.instance === this) {
      Reflect.deleteProperty(SettingsQuery, 'instance')
    }
  }

  /**
   * Gets the entity settings of a specific key
   * @param {EntitySettingsKey<K>} key
   * @param {EntityName} entityType
   * @param {string} entityId
   * @returns {Signal<EntitySettingsValue<K>>}
   * @template K
   */
  queryEntitySettings<K extends keyof SettingsState>(
    key: EntitySettingsKey<K>,
    entityType: EntityName,
    entityId: string
  ): Signal<EntitySettingsValue<K>> {
    return computed(() => {
      const settingState = this.query(key)

      return settingState()?.find(settings => {
        const entitySettings = settings as EntitySettings
        return entitySettings.entityId === entityId && entitySettings.entityName === entityType
      }) as EntitySettingsValue<K>
    })
  }

  getNotificationSettings(): NotificationUserSetting[] {
    return this.getValue().notifications
  }

  /**
   * Gets the view settings of a specific key
   * @param {T} viewSettingsType
   * @returns {Signal<IViewPersistenceSetting[T]>}
   */
  queryViewSetting<T extends keyof IViewPersistenceSetting>(viewSettingsType: T): Signal<IViewPersistenceSetting[T]> {
    return computed(() => {
      const settingState = this.query()
      return settingState().viewSettings?.[viewSettingsType] || this.getViewSettingEmptyValue(viewSettingsType)
    })
  }

  /**
   * Gets the project task list settings for a specific project
   * @param {string} projectId
   * @returns {Signal<IProjectTaskListSetting>}
   */
  queryProjectTaskListSetting(projectId: string): Signal<IProjectTaskListSetting> {
    return computed(() => {
      const settingState = this.query('projectTaskListSettings')
      return settingState()?.find(setting => setting.projectId === projectId) || null
    })
  }

  /**
   * Gets the time tracking tracked/planned settings for a specific project
   * @param {string} projectId
   * @returns {Signal<IProjectTimeTrackingTrackedPlannedSetting>}
   */
  queryProjectTimeTrackingTrackedPlannedSetting(projectId: string): Signal<IProjectTimeTrackingTrackedPlannedSetting> {
    return computed(() => {
      const settingState = this.query('projectTimeTrackingTrackedPlannedSettings')
      return settingState()?.find(setting => setting.projectId === projectId) || null
    })
  }

  /**
   * Gets the entity times settings for a specific entity
   * @param {string} entityId
   * @param {ValidStandardTimeReportEntityType} entityType
   * @param {EntityTimesViewContext} viewContext Optional view context for route-specific settings
   * @returns {Signal<IEntityTimesSettings>}
   */
  queryEntityTimesSettings(
    entityId: string,
    entityType: ValidStandardTimeReportEntityType,
    viewContext?: EntityTimesViewContext
  ): Signal<IEntityTimesSettings> {
    return computed(() => {
      const settingState = this.query('entityTimesSettings')

      let match = settingState()?.find(
        setting =>
          setting.entityId === entityId && setting.entityType === entityType && setting.viewContext === viewContext
      )

      if (match) {
        return match
      }

      // Backward compatibility: fallback to legacy (no viewContext) for entity detail views
      const entityDetailContexts: EntityTimesViewContext[] = ['project-times', 'company-times', 'user-times']

      if (viewContext && entityDetailContexts.includes(viewContext)) {
        match = settingState()?.find(
          setting => setting.entityId === entityId && setting.entityType === entityType && !setting.viewContext
        )
      }

      return match || ({ entityId, entityType, viewContext } as IEntityTimesSettings)
    })
  }

  /**
   * Gets the project settings for a specific project
   * @param {string} projectId
   * @returns {Signal<IProjectViewSetting>}
   */
  queryProjectSetting(projectId: string): Signal<IProjectViewSetting> {
    return computed(() => {
      const settingState = this.query('projectSettings')
      return settingState()?.find(setting => setting.projectId === projectId) || null
    })
  }

  /**
   * Gets the project task list settings for a specific project
   * @param {string} projectId
   * @returns {IProjectTaskListSetting}
   */
  getProjectTaskListSetting(projectId: string): IProjectTaskListSetting {
    const settingState = this.getValue()
    return settingState.projectTaskListSettings
      ? settingState.projectTaskListSettings.find(setting => setting.projectId === projectId)
      : null
  }

  /**
   * Gets the time tracking tracked/planned settings for a specific project
   * @param {string} projectId
   * @returns {IProjectTimeTrackingTrackedPlannedSetting}
   */
  getProjectTimeTrackingTrackedPlannedSetting(projectId: string): IProjectTimeTrackingTrackedPlannedSetting {
    const settingState = this.getValue()
    return settingState.projectTimeTrackingTrackedPlannedSettings
      ? settingState.projectTimeTrackingTrackedPlannedSettings.find(setting => setting.projectId === projectId)
      : null
  }

  /**
   * gets the user setting dynamicDurationFormat
   * @returns {DurationFormatType}
   */
  getDynamicDurationSetting(): DurationFormatType {
    if (this.getValue().viewSettings?.hasOwnProperty('dynamicDurationFormat')) {
      return this.getValue().viewSettings.dynamicDurationFormat
    }

    return null
  }

  /**
   * Gets the user setting userNameDisplayFormat
   * @returns {UserNameDisplayFormat} The user name display format, defaults to 'full_name'
   */
  getUserNameDisplaySetting(): UserNameDisplayFormat {
    if (this.getValue().viewSettings?.hasOwnProperty('userNameDisplayFormat')) {
      return this.getValue().viewSettings.userNameDisplayFormat
    }

    return 'full_name'
  }

  /**
   * Queries the user name display format setting as a signal
   * @returns {Signal<UserNameDisplayFormat>} Signal with the user name display format, defaults to 'full_name'
   */
  queryUserNameDisplaySetting(): Signal<UserNameDisplayFormat> {
    return computed(() => {
      const viewSettings = this.query('viewSettings')
      return viewSettings()?.userNameDisplayFormat || 'full_name'
    })
  }

  /**
   * Gets the view settings of an specific key
   * @param {T} viewSettingsType
   * @returns {IViewPersistenceSetting[T] | undefined}
   */
  getViewSetting<T extends keyof IViewPersistenceSetting>(viewSettingsType: T): IViewPersistenceSetting[T] | undefined {
    if (this.getValue().viewSettings && this.getValue().viewSettings.hasOwnProperty(viewSettingsType)) {
      return this.getValue().viewSettings[viewSettingsType]
    }

    return this.getViewSettingEmptyValue(viewSettingsType)
  }

  /**
   * Gets the view setting empty value depending on the type
   * @param {T} viewSettingsType
   * @returns {IViewPersistenceSetting[T] | undefined}
   */
  private getViewSettingEmptyValue<T extends keyof IViewPersistenceSetting>(
    viewSettingsType: T
  ): IViewPersistenceSetting[T] | undefined {
    if (VIEW_PERSISTENCE_USER_SETTINGS.includes(viewSettingsType)) {
      return [] as IViewPersistenceSetting[T]
    } else if (VIEW_PERSISTENCE_VALUE_SETTINGS.includes(viewSettingsType)) {
      return undefined
    } else {
      return {} as IViewPersistenceSetting[T]
    }
  }

  /**
   * Gets the view settings
   * @returns {Signal<IViewPersistenceSetting>}
   */
  queryViewSettings(): Signal<IViewPersistenceSetting> {
    return computed(() => {
      const viewSetting = this.query('viewSettings')
      return viewSetting() || {}
    })
  }

  /**
   * Gets the framework's view settings.
   * If there are no settings, it returns default settings values.
   * @returns {Signal<IFrameworkViewSetting>}
   */
  queryFrameworkSettings(): Signal<IFrameworkViewSetting> {
    return computed(() => {
      const viewSettings = this.query('viewSettings')
      const storedSettings = viewSettings().framework || {}

      return this.normalizeFrameworkViewSettings({ ...DEFAULT_FRAMEWORK_VIEW_SETTINGS, ...storedSettings })
    })
  }

  /**
   * Gets the framework's view settings.
   * If there are no settings, it returns default settings values.
   * @returns {IFrameworkViewSetting}
   */
  getFrameworkSettings(): IFrameworkViewSetting {
    const storedSettings = this.getValue().viewSettings?.framework || {}
    return this.normalizeFrameworkViewSettings({ ...DEFAULT_FRAMEWORK_VIEW_SETTINGS, ...storedSettings })
  }

  /**
   * Normalizes framework settings values that can become inconsistent in persisted data.
   * @param {IFrameworkViewSetting} settings
   * @returns {IFrameworkViewSetting}
   */
  private normalizeFrameworkViewSettings(settings: IFrameworkViewSetting): IFrameworkViewSetting {
    if (!settings?.pinnedProjects?.length) {
      return settings
    }

    return {
      ...settings,
      pinnedProjects: [...new Set(settings.pinnedProjects)]
    }
  }

  /**
   * Gets the desktop app settings
   * @returns {IDesktopAppUserSetting}
   */
  getDesktopAppSettings(): IDesktopAppUserSetting {
    return this.getValue().desktopApp
  }

  /**
   * Gets the visible columns for an entity list
   * @param {ListEntity} listEntity
   * @param {string} projectId
   * @returns {Signal<{ smallList: string[]; largeList: string[]; }>}
   */
  queryListColumns(listEntity: ListEntity, projectId?: string): Signal<{ smallList: string[]; largeList: string[] }> {
    return computed(() => {
      const viewSettings = this.query('viewSettings')
      const listColumns = viewSettings()?.listColumns

      const smallList = listColumns?.smallList?.[listEntity]
      const largeList = listColumns?.largeList?.[listEntity]

      if (listEntity === 'projectTask') {
        return {
          smallList: smallList?.[projectId || 'default'],
          largeList: largeList?.[projectId || 'default']
        }
      }

      return { smallList, largeList }
    })
  }

  /**
   * Get all view settings
   * @returns {IViewPersistenceSetting}
   */
  getViewSettings(): IViewPersistenceSetting {
    return this.getValue().viewSettings || {}
  }

  /**
   * Query Task View List Settings
   * @returns {Signal<ITaskViewListsSetting[]>}
   */
  queryTaskViewListSettings(): Signal<ITaskViewListsSetting[]> {
    return computed(() => {
      const setting = this.query('taskViewListsSettings')
      return setting() || []
    })
  }

  /**
   * Queries taskListWidget Settings
   * @returns {Signal<ITaskViewListsSetting[]>}
   */
  queryTaskListWidgetSettings(): Signal<ITaskViewListsSetting[]> {
    return computed(() => {
      const setting = this.query('taskListWidgetSettings')
      return setting() || null
    })
  }

  /**
   * Select Sorting Grouping Options
   * @returns {IGroupingSorting}
   */
  getSortingGroupingOptions(): IGroupingSorting {
    return this.getValue().sortingGrouping || {}
  }
}
